import {
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  inject,
  OnInit,
  effect,
} from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { AuthService, User } from '../../core/services/auth.service';
import {
  UserSettingsService,
  UserProfile,
} from '../../core/services/user-settings.service';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';

interface Role {
  code: string;
  name: string;
}

@Component({
  selector: 'app-user-profile',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    AppButtonComponent,
    SelectModule,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.css'],
})
export class UserProfileComponent implements OnInit {
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);
  private userSettingsService = inject(UserSettingsService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private location = inject(Location);
  private fb = inject(FormBuilder);

  // Get user profile from AuthService instead of UserSettingsService
  userProfile: UserProfile = this.convertAuthUserToProfile(
    this.authService.currentUser()
  );

  availableRoles: Role[] = [
    { code: 'Administrator', name: 'Administrator' },
    { code: 'Manager', name: 'Manager' },
    { code: 'User', name: 'User' },
  ];

  @Output() profileUpdated = new EventEmitter<UserProfile>();

  isEditingProfile = signal(false);
  isSavingProfile = signal(false);
  profileForm!: FormGroup;

  private originalProfile: Partial<UserProfile> = {};

  ngOnInit(): void {
    // Initialize with current user from AuthService
    this.loadUserProfile();
    this.initializeProfileForm();
    this.originalProfile = { ...this.userProfile };
    this.updateRolesWithTranslations();

    // Subscribe to AuthService user changes
    effect(() => {
      const user = this.authService.currentUser(); // <-- signal
      if (user) {
        this.userProfile = this.convertAuthUserToProfile(user);
        this.initializeProfileForm();
        this.originalProfile = { ...this.userProfile };
      }
    });
  }

  /**
   * Convert AuthService User to UserProfile format
   */
  private convertAuthUserToProfile(authUser: User | null): UserProfile {
    if (!authUser) {
      // Fallback profile if no user is authenticated
      return {
        id: '1',
        username: 'Guest User',
        email: 'guest@example.com',
        avatar: '',
        role: 'User',
        lastLogin: new Date(),
        isActive: true,
      };
    }

    return {
      id: authUser.id,
      username: authUser.username,
      email: authUser.email,
      avatar: authUser.avatar || '/assets/images/default-avatar.png',
      role: this.translateUserRole(authUser.role),
      lastLogin: new Date(), // We could store this in AuthService if needed
      isActive: true,
    };
  }

  /**
   * Load user profile from AuthService
   */
  private loadUserProfile(): void {
    const currentUser = this.authService.currentUser();
    if (currentUser) {
      this.userProfile = this.convertAuthUserToProfile(currentUser);
    }
  }

  private updateRolesWithTranslations(): void {
    this.availableRoles = [
      {
        code: 'Administrator',
        name:
          this.translateService.instant('user_profile.roles.administrator') ||
          'Administrator',
      },
      {
        code: 'Manager',
        name:
          this.translateService.instant('user_profile.roles.manager') ||
          'Manager',
      },
      {
        code: 'User',
        name:
          this.translateService.instant('user_profile.roles.user') || 'User',
      },
    ];
  }

  /**
   * Translate user role
   */
  private translateUserRole(role: string): string {
    const roleKey = role.toLowerCase();
    switch (roleKey) {
      case 'administrator':
      case 'admin':
        return (
          this.translateService.instant('user_profile.roles.administrator') ||
          'Administrator'
        );
      case 'manager':
        return (
          this.translateService.instant('user_profile.roles.manager') ||
          'Manager'
        );
      case 'user':
        return (
          this.translateService.instant('user_profile.roles.user') || 'User'
        );
      default:
        return role;
    }
  }

  private initializeProfileForm(): void {
    this.profileForm = this.fb.group({
      username: [
        this.userProfile.username,
        [Validators.required, Validators.minLength(3)],
      ],
      email: [this.userProfile.email, [Validators.required, Validators.email]],
      role: [this.userProfile.role, [Validators.required]],
    });
  }

  toggleProfileEdit(): void {
    if (this.isEditingProfile()) {
      this.cancelProfileEdit();
    } else {
      this.isEditingProfile.set(true);
      this.initializeProfileForm();
    }
  }

  saveProfile(): void {
    if (this.profileForm.valid) {
      this.isSavingProfile.set(true);

      const updatedProfile = {
        ...this.userProfile,
        ...this.profileForm.value,
      };

      // Convert back to AuthService User format and update
      const updatedUser: User = {
        id: updatedProfile.id,
        username: updatedProfile.username,
        email: updatedProfile.email,
        role: updatedProfile.role,
        avatar: updatedProfile.avatar || '/assets/images/default-avatar.png',
      };

      setTimeout(() => {
        // Update both services
        this.userSettingsService.updateUserProfile(updatedProfile);

        // Update AuthService - it will sync with Keycloak and update localStorage
        // No need to directly manipulate localStorage here
        this.authService.syncWithKeycloak().catch((error) => {
          console.error('[UserProfile] Failed to sync with Keycloak:', error);
        });

        this.userProfile = updatedProfile;
        this.originalProfile = { ...updatedProfile };

        this.isEditingProfile.set(false);
        this.isSavingProfile.set(false);

        this.profileUpdated.emit(updatedProfile);

        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant(
            'user_profile.messages.profile_updated'
          ),
          detail: this.translateService.instant(
            'user_profile.messages.profile_updated_detail'
          ),
          life: 3000,
        });
      }, 1000);
    }
  }

  cancelProfileEdit(): void {
    this.isEditingProfile.set(false);
    this.profileForm.reset();
  }

  goBack(): void {
    this.location.back();
  }
}
